import React, { useState, useEffect } from 'react'
import { auth, db, googleProvider } from '../firebase'
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithEmailAndPassword,
  linkWithCredential,
  GoogleAuthProvider,
} from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getDoc, doc, collection, query, where, getDocs } from 'firebase/firestore'
import { getBrandImage } from '../data/brandImages'
import { normalizarTelefono } from '../utils/phoneUtils'
import { trackEvento, EVENTOS } from '../utils/analytics'

const iniciarOnboardingFn = httpsCallable(getFunctions(undefined, 'us-central1'), 'iniciarOnboarding')

/**
 * Registro con email/contraseña o Google.
 * Campos obligatorios (email/pass): email, contraseña, nombre, teléfono.
 * Con Google: App.js detecta perfil incompleto → GoogleOnboarding pide teléfono.
 */

const LINK_STATE = { NONE: 'none', OFFERING: 'offering', PASSWORD: 'password' }

const ERROR_LEGIBLE = {
  'auth/popup-closed-by-user':    'Cerraste la ventana de Google antes de completar el registro.',
  'auth/popup-blocked':           'El navegador bloqueó la ventana de Google. Permitila e intentá de nuevo.',
  'auth/cancelled-popup-request': null,
  'auth/network-request-failed':  'Sin conexión. Revisá tu internet e intentá de nuevo.',
}
function mensajeGoogle(code) {
  return ERROR_LEGIBLE[code] ?? 'Ocurrió un error al autenticar con Google. Intentá de nuevo.'
}

const ClientRegistration = ({ onRegisterSuccess, siteConfig, setCurrentView }) => {
  const [form, setForm] = useState({
    email: '', password: '', nombre: '', telefono: '',
    apellido: '', dni: '', nacimiento: '', domicilio: '',
  })
  const [error, setError]         = useState('')
  const [success, setSuccess]     = useState('')
  const [registrando, setRegistrando] = useState(false)
  const [mostrarOpcionales, setMostrarOpcionales] = useState(false)

  // Estado de vinculación: Google con email que ya existe como password
  const [linkState, setLinkState]               = useState(LINK_STATE.NONE)
  const [pendingCredential, setPendingCredential] = useState(null)
  const [linkEmail, setLinkEmail]               = useState('')
  const [linkPassword, setLinkPassword]         = useState('')

  const logo = getBrandImage(siteConfig, 'logo')
  const hero = getBrandImage(siteConfig, 'heroComoFunciona')

  useEffect(() => { trackEvento(EVENTOS.REGISTRATION_VIEW) }, [])

  // ─── Google Sign-In / Sign-Up ─────────────────────────────────────────────
  // Si el usuario ya tiene cuenta → App.js lo detecta y lo lleva a home.
  // Si es nuevo → App.js detecta doc ausente en usuarios/ → GoogleOnboarding.
  const handleGoogle = async () => {
    setError('')
    trackEvento(EVENTOS.LOGIN_GOOGLE_STARTED)
    setRegistrando(true)
    try {
      await signInWithPopup(auth, googleProvider)
      trackEvento(EVENTOS.LOGIN_GOOGLE_SUCCESS)
      if (onRegisterSuccess) onRegisterSuccess()
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        // Email ya existe con email/password → ofrecer vincular
        const credential = GoogleAuthProvider.credentialFromError(err)
        setPendingCredential(credential)
        setLinkEmail(err.customData?.email || '')
        setLinkState(LINK_STATE.OFFERING)
        trackEvento(EVENTOS.ACCOUNT_LINK_OFFERED, { metodo: 'google' })
        setRegistrando(false)
        return
      }
      if (err.code !== 'auth/cancelled-popup-request') {
        trackEvento(EVENTOS.LOGIN_GOOGLE_FAILED, { code: err.code })
        const msg = mensajeGoogle(err.code)
        if (msg) setError(msg)
      }
    } finally {
      setRegistrando(false)
    }
  }

  // ─── Vincular Google a cuenta email/password existente ───────────────────
  const handleLinkAccount = async (e) => {
    e.preventDefault()
    if (!linkPassword) { setError('Ingresá tu contraseña para vincular las cuentas.'); return }
    setRegistrando(true)
    setError('')
    try {
      const result = await signInWithEmailAndPassword(auth, linkEmail, linkPassword)
      await linkWithCredential(result.user, pendingCredential)
      trackEvento(EVENTOS.ACCOUNT_LINK_COMPLETED)
      setLinkState(LINK_STATE.NONE)
      setPendingCredential(null)
      if (onRegisterSuccess) onRegisterSuccess()
    } catch (err) {
      trackEvento(EVENTOS.LOGIN_EMAIL_FAILED, { code: err.code, contexto: 'link' })
      setError('Contraseña incorrecta. Verificá e intentá de nuevo.')
    } finally {
      setRegistrando(false)
    }
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setError('')
    setSuccess('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    trackEvento(EVENTOS.REGISTRATION_STARTED)
    setRegistrando(true)

    try {
      // Pre-check de DNI duplicado (UX solamente — la CF hace el check autoritativo)
      if (form.dni.trim()) {
        try {
          const q = query(collection(db, 'usuarios'), where('dni', '==', form.dni.trim()))
          const snapshot = await getDocs(q)
          if (!snapshot.empty) {
            setError('El DNI ya está registrado. Si ya sos miembro, iniciá sesión.')
            setRegistrando(false)
            return
          }
        } catch {}
      }

      // 1. Crear cuenta en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password)

      // 2. Inicializar perfil via CF — el servidor decide puntos, rol y campos sensibles
      const result = await iniciarOnboardingFn({
        nombre:     form.nombre.trim(),
        apellido:   form.apellido.trim(),
        telefono:   form.telefono,
        dni:        form.dni.trim(),
        nacimiento: form.nacimiento,
        domicilio:  form.domicilio.trim(),
      })

      const puntosBienvenida = result.data?.puntosBienvenida || 0

      trackEvento(EVENTOS.REGISTRATION_COMPLETED)
      setRegistrando(false)
      setSuccess(
        puntosBienvenida > 0
          ? `¡Bienvenido! Se acreditaron ${puntosBienvenida} puntos.`
          : '¡Registro exitoso! Ahora podés iniciar sesión.'
      )
      if (onRegisterSuccess) onRegisterSuccess()
      setForm({ email: '', password: '', nombre: '', telefono: '', apellido: '', dni: '', nacimiento: '', domicilio: '' })
    } catch (err) {
      trackEvento(EVENTOS.REGISTRATION_FAILED, { code: err.code })
      setRegistrando(false)
      if (err.code === 'auth/email-already-in-use') {
        setError('Este email ya está registrado. ¿Querés iniciar sesión?')
      } else {
        setError('Error al registrar la cuenta. Verificá los datos e intentá de nuevo.')
      }
    }
  }

  const fc = 'w-full h-11 px-4 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red'

  // ─── UI: pantalla de vinculación ─────────────────────────────────────────
  if (linkState === LINK_STATE.OFFERING || linkState === LINK_STATE.PASSWORD) {
    return (
      <div className="grid lg:grid-cols-2 min-h-[78vh] mx-3 sm:mx-6 rounded-[2rem] overflow-hidden bg-white/80 border border-black/5">
        <div className="hidden lg:block relative min-h-[520px]">
          <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
          <p className="absolute bottom-8 left-8 right-8 text-white font-script text-3xl">
            Quiero sumarme a Maja Club
          </p>
        </div>
        <div className="flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            <img src={logo} alt="Maja Morena" className="h-14 mx-auto mb-4" />
            <h2 className="text-center font-display text-3xl tracking-tight">Ya tenés cuenta Maja</h2>
            <p className="mt-2 text-center text-sm text-maja-ink/60">
              Ya existe una cuenta con <b>{linkEmail}</b>.
              Ingresá tu contraseña para vincular Google a tu cuenta existente.
            </p>
            <form className="mt-6 space-y-4" onSubmit={handleLinkAccount}>
              <div className="px-4 py-3 bg-black/5 rounded-2xl text-sm text-maja-ink/70">{linkEmail}</div>
              <input
                type="password" placeholder="Tu contraseña actual"
                value={linkPassword}
                onChange={(e) => { setLinkPassword(e.target.value); setError('') }}
                className="w-full h-12 px-4 rounded-2xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-maja-red"
                required
              />
              {error && <p className="text-sm text-maja-red">{error}</p>}
              <button type="submit" disabled={registrando}
                className="w-full h-12 rounded-full bg-maja-red text-white font-semibold disabled:opacity-60">
                {registrando ? 'Vinculando...' : 'Vincular mi cuenta con Google'}
              </button>
              <button type="button"
                onClick={() => { setLinkState(LINK_STATE.NONE); setPendingCredential(null) }}
                className="w-full h-11 rounded-full border border-black/10 text-maja-ink font-semibold">
                Cancelar
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // ─── UI: registro normal ──────────────────────────────────────────────────
  return (
    <div className="grid lg:grid-cols-2 min-h-[78vh] mx-3 sm:mx-6 rounded-[2rem] overflow-hidden bg-white/80 border border-black/5">
      <div className="hidden lg:block relative min-h-[520px]">
        <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <p className="absolute bottom-8 left-8 right-8 text-white font-script text-3xl">
          Quiero sumarme a Maja Club
        </p>
      </div>
      <div className="px-6 py-10 flex items-center justify-center">
        <div className="w-full max-w-md">
          <img src={logo} alt="Maja Morena" className="h-14 mx-auto mb-3" />
          <h2 className="text-center font-display text-4xl tracking-tight">Creá tu cuenta</h2>
          <p className="text-center text-sm text-maja-ink/60 mt-1 mb-4">
            Es gratis y empezás a sumar puntos
          </p>

          {/* Google */}
          <button
            type="button"
            onClick={handleGoogle}
            disabled={registrando}
            className="w-full h-12 rounded-full border border-black/15 bg-white font-semibold text-maja-ink flex items-center justify-center gap-3 hover:bg-black/5 disabled:opacity-60 transition"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.9 2.3 30.3 0 24 0 14.7 0 6.7 5.4 2.8 13.3l7.8 6.1C12.5 13 17.8 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.5 5.8c4.4-4 6.9-10 6.9-17z"/>
              <path fill="#FBBC05" d="M10.6 28.6A14.4 14.4 0 0 1 9.5 24c0-1.6.3-3.1.7-4.6l-7.8-6.1A23.9 23.9 0 0 0 0 24c0 3.9.9 7.5 2.5 10.8l8.1-6.2z"/>
              <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.2 0-11.5-4.2-13.4-9.9l-8.1 6.2C6.7 42.6 14.7 48 24 48z"/>
            </svg>
            Continuar con Google
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-black/8" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-maja-ink/30">o</span>
            <div className="flex-1 h-px bg-black/8" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Obligatorios */}
            <input type="email"    name="email"    placeholder="Email"    value={form.email}    onChange={handleChange} required className={fc} />
            <input type="password" name="password" placeholder="Contraseña (mínimo 6 caracteres)" value={form.password} onChange={handleChange} required minLength={6} className={fc} />
            <input type="text"     name="nombre"   placeholder="Nombre"   value={form.nombre}   onChange={handleChange} required className={fc} />
            <input type="tel"      name="telefono" placeholder="Teléfono celular (ej: 3541 123 456)" value={form.telefono} onChange={handleChange} required className={fc} />

            {/* Opcionales expandibles */}
            <button type="button"
              onClick={() => setMostrarOpcionales(!mostrarOpcionales)}
              className="w-full text-left text-sm font-semibold text-maja-ink/50 flex items-center gap-2 py-1">
              <span>{mostrarOpcionales ? '▲' : '▼'}</span>
              {mostrarOpcionales ? 'Ocultar datos opcionales' : 'Completar más datos (opcional)'}
            </button>

            {mostrarOpcionales && (
              <div className="space-y-3 pt-1">
                <input type="text" name="apellido"  placeholder="Apellido"  value={form.apellido}  onChange={handleChange} className={fc} />
                <input type="text" name="dni"       placeholder="DNI"       value={form.dni}       onChange={handleChange} className={fc} />
                <div>
                  <label className="block mb-1 text-sm font-semibold text-maja-ink/70">
                    Fecha de nacimiento
                    <span className="ml-1 text-[11px] font-normal text-maja-ink/40">(para tu beneficio de cumpleaños)</span>
                  </label>
                  <input type="date" name="nacimiento" value={form.nacimiento} onChange={handleChange} className={fc} />
                </div>
                <input type="text" name="domicilio" placeholder="Domicilio" value={form.domicilio} onChange={handleChange} className={fc} />
              </div>
            )}

            <button type="submit" disabled={registrando}
              className="w-full h-12 rounded-full bg-maja-red text-white font-semibold disabled:opacity-60">
              {registrando ? 'Registrando...' : 'Registrarme gratis'}
            </button>

            {error   && (
              <div>
                <p className="text-maja-red text-sm">{error}</p>
                {error.includes('iniciar sesión') && (
                  <button type="button" onClick={() => setCurrentView('login')}
                    className="mt-1 text-sm font-semibold text-maja-red underline">
                    Ir a iniciar sesión
                  </button>
                )}
              </div>
            )}
            {success && <p className="text-emerald-700 text-sm">{success}</p>}
          </form>

          {setCurrentView && (
            <p className="text-center text-sm text-maja-ink/60 mt-6">
              ¿Ya sos miembro?{' '}
              <button onClick={() => setCurrentView('login')} className="font-semibold text-maja-red">
                Iniciar sesión
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default ClientRegistration
